// The energy of one side as a row of pips, then the count. A pip is a coin:
// the round mark of one energy, the same on the die, in the air, and here. A
// pip that fills pops and sounds, one after the other, so the player sees the
// energy come. Energy over the row shows in the count alone, and the count
// pops for it. Energy the side pays leaves from the last pip down, one after
// the other, and each pip sounds as it empties.

import { ENERGY_PIP_COUNT, ENERGY_PIP_STEP_MS, ENERGY_SPEND_STEP_MS } from '../constants';
import type { Side } from '../engine/types';
import { el } from '../lib/dom';
import { energyInForm } from '../sound/moments';
import { playSound } from '../sound/player';

export type EnergyPips = {
  root: HTMLElement;
  /**
   * `quiet` fills the pips with no pop and no sound: a loaded match, or an
   * earlier step. `capacity` is the most the side can hold, and the row has
   * that many pips. In Godsmode it is the room on the pieces of the side, so
   * it changes when a piece that holds energy leaves or comes.
   */
  draw: (energy: number, quiet: boolean, capacity?: number) => void;
};

const FRESH_PIP_CLASS = 'pips__pip--fresh';
const FRESH_COUNT_CLASS = 'pips__count--fresh';
const LEAVING_PIP_CLASS = 'pips__pip--leaving';
/** The side whose energy sounds a fourth lower. */
const LOW_SIDE: Side = 'violet';

/** Where one energy of a side shows: each pip of each row of that side, and the count of the row. */
type Row = { pips: () => readonly HTMLElement[]; count: HTMLElement };
const rows: Record<Side, Row[]> = { gold: [], violet: [] };

/**
 * The box on the screen of the pip at `place` in the row of `side`, from zero.
 * For energy over the row, and for a pip the row hides, it is the box of the
 * count. The phone shows one pip of Moon. Null when no row of the side is on
 * the screen.
 */
export const energyTarget = (side: Side, place: number): DOMRect | null => {
  for (const row of rows[side]) {
    const pipBox = row.pips()[place]?.getBoundingClientRect();
    const box = pipBox && pipBox.width > 0 ? pipBox : row.count.getBoundingClientRect();
    if (box.width > 0) return box;
  }
  return null;
};

/**
 * Starts the motion of a class from its first frame, also for a thing that
 * has the class. The read between the two changes makes the browser see both.
 * The class is on in the same frame, so a pip that waits to empty shows no
 * empty frame first.
 */
const pop = (element: HTMLElement, className: string, delayMs: number): void => {
  element.classList.remove(className);
  element.style.animationDelay = `${delayMs}ms`;
  void element.offsetWidth;
  element.classList.add(className);
};

export const createEnergyPips = (side: Side): EnergyPips => {
  let pips: HTMLElement[] = [];
  const row = el('span', { className: 'pips__row', ariaHidden: 'true' });
  const count = el('span', { className: 'pips__count' });
  const root = el('span', { className: `pips pips--${side}` }, [row, count]);
  // The row gets new pips when the capacity changes, so a coin asks for the pips of the moment.
  rows[side].push({ pips: () => pips, count });
  let shown: number | null = null;

  /** Makes the row as long as the capacity. A row of a new length fills with no pop. */
  const fit = (capacity: number): boolean => {
    if (pips.length === capacity) return false;
    pips = Array.from({ length: capacity }, () => el('i', { className: 'pips__pip' }));
    row.replaceChildren(...pips);
    return true;
  };

  const draw = (energy: number, quiet: boolean, capacity = ENERGY_PIP_COUNT): void => {
    const resized = fit(Number.isFinite(capacity) ? capacity : ENERGY_PIP_COUNT);
    if (energy === shown && !resized) return;
    const before = resized ? energy : (shown ?? energy);
    shown = energy;
    count.textContent = String(energy);
    root.ariaLabel = `Energy ${energy} of ${pips.length}`;
    pips.forEach((pip, place) => {
      pip.classList.toggle('pips__pip--on', place < energy);
      // A pip that is still full keeps its pop, so the second coin of a roll does not cut the pop of the first.
      if (place >= energy || quiet) pip.classList.remove(FRESH_PIP_CLASS);
      pip.classList.remove(LEAVING_PIP_CLASS);
    });
    if (quiet) {
      count.classList.remove(FRESH_COUNT_CLASS);
      return;
    }
    // Each energy the side gained, one after the other: its pip pops, or the count does.
    for (let place = Math.max(before, 0); place < energy; place += 1) {
      const delayMs = (place - before) * ENERGY_PIP_STEP_MS;
      const pip = pips[place];
      if (pip) pop(pip, FRESH_PIP_CLASS, delayMs);
      else pop(count, FRESH_COUNT_CLASS, delayMs);
      playSound('energyIn', delayMs, energyInForm(place, side === LOW_SIDE));
    }
    // Each energy the side paid, from the last down: its pip stays full until its moment, then empties.
    for (let place = before - 1; place >= energy; place -= 1) {
      const delayMs = (before - 1 - place) * ENERGY_SPEND_STEP_MS;
      const pip = pips[place];
      if (pip) pop(pip, LEAVING_PIP_CLASS, delayMs);
      playSound('energySpend', delayMs, energyInForm(place, side === LOW_SIDE));
    }
  };

  return { root, draw };
};
